import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as Finance } from "../../assets/svgIcon/menuIcon/Accounting.svg";
import { ReactComponent as Dashboard } from "../../assets/svgIcon/menuIcon/Dashboard.svg";
import { ReactComponent as Integrations } from "../../assets/svgIcon/menuIcon/Integrations.svg";
import { ReactComponent as Inventory } from "../../assets/svgIcon/menuIcon/Inventory.svg";
import { ReactComponent as Master } from "../../assets/svgIcon/menuIcon/Masters.svg";
import { ReactComponent as Logo } from "../../assets/svgIcon/menuIcon/orderlink.svg";
import { ReactComponent as Planner } from "../../assets/svgIcon/menuIcon/Planner.svg";
import { ReactComponent as Production } from "../../assets/svgIcon/menuIcon/Production.svg";
import { ReactComponent as Orders } from "../../assets/svgIcon/menuIcon/Purchases.svg";
import { ReactComponent as Sales } from "../../assets/svgIcon/menuIcon/Sale.svg";
import { ReactComponent as Shipments } from "../../assets/svgIcon/menuIcon/Shipments.svg";
import { ReactComponent as SearchIcon } from "../../assets/svgIcon/topBar/Search.svg";
import {
  findActiveNavigation,
  SIDEBAR_CATALOG_BY_KEY,
} from "../../navigation/navigationCatalog";
import { useNavigationLayout } from "../../../../hooks/useNavigationLayout";
import { MAIN_MENU_WIDTH } from "../layoutConstants";
import { QuickJump } from "../QuickJump/QuickJump";
import { SubMenu } from "../SubMenu/SubMenu";

export { MAIN_MENU_WIDTH };

const SIDEBAR_ICONS = {
  reports: <Dashboard />,
  inventory: <Inventory />,
  sales: <Sales />,
  planner: <Planner />,
  orders: <Orders />,
  production: <Production />,
  shipments: <Shipments />,
  finance: <Finance />,
  master: <Master />,
  integrations: <Integrations />,
};

/**
 * MainMenu
 *
 * Left-hand module rail for the new layout. Users pick a product area
 * (Orders, Inventory, Integrations, …) and, when that area has pages,
 * a fly-out submenu opens beside the rail.
 */
export function MainMenu() {
  const [isOpenSubMenu, setIsOpenSubMenu] = useState(false);
  const [selectedMenuKey, setSelectedMenuKey] = useState("");
  const [isQuickJumpOpen, setIsQuickJumpOpen] = useState(false);
  const moduleTriggerRefs = useRef(/** @type {Record<string, HTMLElement | null>} */ ({}));
  const { items: visibleModules } = useNavigationLayout("sidebar");

  const { pathname } = useLocation();

  const activeNavigation = useMemo(
    () => findActiveNavigation(pathname, visibleModules),
    [pathname, visibleModules],
  );

  const closeSubMenu = useCallback(() => {
    setIsOpenSubMenu(false);
  }, []);

  const openQuickJump = useCallback(() => {
    setIsQuickJumpOpen(true);
  }, []);

  const closeQuickJump = useCallback(() => {
    setIsQuickJumpOpen(false);
  }, []);

  useEffect(() => {
    const onKeyDown = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openQuickJump();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openQuickJump]);

  const toggleSubMenu = moduleKey => {
    setSelectedMenuKey(moduleKey);
    const meta = SIDEBAR_CATALOG_BY_KEY[moduleKey];
    if (meta?.children?.length) {
      setIsOpenSubMenu(true);
    } else {
      closeSubMenu();
    }
  };

  const selectedSubMenuItems = useMemo(() => {
    const moduleEntry = visibleModules.find(mod => mod.key === selectedMenuKey);
    return moduleEntry?.children ?? [];
  }, [selectedMenuKey, visibleModules]);

  const selectedMenuLabel = SIDEBAR_CATALOG_BY_KEY[selectedMenuKey]?.label ?? selectedMenuKey;
  const subMenuPanelId = selectedMenuKey ? `submenu-panel-${selectedMenuKey}` : "";

  return (
    <MainMenuContainer>
      <nav className="mainMenu" aria-label="Application modules">
        <Link key="logo" className="logo" to="/dashboard" aria-label="Orderlink home">
          <Logo />
        </Link>

        <button
          type="button"
          className="quickJumpTrigger"
          onClick={openQuickJump}
          aria-label="Jump to page (Ctrl+K)"
          title="Jump to (Ctrl+K)"
        >
          <SearchIcon aria-hidden />
        </button>

        <div className="menuContainer">
          {visibleModules.map(module => {
            const { key, label, url } = module;
            const icon = SIDEBAR_ICONS[key];
            const opensSubmenu = (module.children?.length ?? 0) > 0;
            const isActiveModule = key === activeNavigation.moduleKey;
            const isOpenModule = isOpenSubMenu && key === selectedMenuKey;
            const itemClassName = [
              "iconWrapper",
              isActiveModule ? "activeMenu" : "",
              isOpenModule && !isActiveModule ? "openMenu" : "",
            ]
              .filter(Boolean)
              .join(" ");

            if (opensSubmenu) {
              return (
                <button
                  key={key}
                  type="button"
                  ref={el => {
                    moduleTriggerRefs.current[key] = el;
                  }}
                  onClick={() => toggleSubMenu(key)}
                  data-menu={key}
                  className={itemClassName}
                  aria-expanded={isOpenModule}
                  aria-controls={isOpenModule ? subMenuPanelId : undefined}
                  aria-current={isActiveModule ? "page" : undefined}
                >
                  <span className="iconSlot">{icon}</span>
                  <span className="menuLabel">{label}</span>
                  <span className="submenuChevron" aria-hidden>
                    ›
                  </span>
                </button>
              );
            }

            return (
              <Link
                key={key}
                ref={el => {
                  moduleTriggerRefs.current[key] = el;
                }}
                onClick={() => closeSubMenu()}
                data-menu={key}
                className={itemClassName}
                to={url}
                aria-current={isActiveModule ? "page" : undefined}
              >
                <span className="iconSlot">{icon}</span>
                <span className="menuLabel">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      {isOpenSubMenu ? (
        <SubMenu
          subMenuItems={selectedSubMenuItems}
          closeSubMenu={closeSubMenu}
          moduleLabel={selectedMenuLabel}
          activeChildKey={activeNavigation.childKey}
          panelId={subMenuPanelId}
          moduleTriggerRefs={moduleTriggerRefs}
          returnFocusKey={selectedMenuKey}
        />
      ) : null}
      <QuickJump isOpen={isQuickJumpOpen} onClose={closeQuickJump} />
    </MainMenuContainer>
  );
}

const MainMenuContainer = styled.div`
  display: flex;
  z-index: 501;
  height: 100vh;
  height: 100dvh;
  flex-shrink: 0;
  overflow: hidden;

  > .mainMenu {
    display: flex;
    width: ${MAIN_MENU_WIDTH}px;
    flex-direction: column;
    align-items: center;
    background: var(--DARK-GREY, #0e0e2c);
    height: 100%;
    position: sticky;
    top: 0;
    overflow: hidden;

    > .logo {
      display: flex;
      height: 72px;
      padding: 20px 16px;
      justify-content: center;
      align-items: center;
      align-self: stretch;
      flex-shrink: 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      --menu-icon-accent: #8fb4ff;
      color: #fff;
    }

    > .quickJumpTrigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 36px;
      margin: 8px 0 4px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.04);
      color: #c9cbe0;
      cursor: pointer;
      flex-shrink: 0;

      svg {
        width: 18px;
        height: 18px;
      }

      &:hover,
      &:focus-visible {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.2);
        color: #fff;
        outline: none;
      }
    }

    > .menuContainer {
      display: flex;
      padding: 4px 8px 12px;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 4px;
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      .iconWrapper {
        position: relative;
        display: flex;
        padding: 6px 4px 4px;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        border-radius: 8px;
        border: none;
        width: calc(${MAIN_MENU_WIDTH}px - 16px);
        min-height: 60px;
        flex: 0 0 auto;
        box-sizing: border-box;
        overflow: visible;
        text-decoration: none;
        color: #c9cbe0;
        background: transparent;
        cursor: pointer;
        font: inherit;
        --menu-icon-accent: #8fb4ff;

        .iconSlot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;

          svg {
            width: 22px;
            height: 22px;
            max-height: 22px;
          }
        }

        .menuLabel {
          color: #c9cbe0;
          font-family: Inter, sans-serif;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.2;
          text-align: center;
          width: 100%;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .submenuChevron {
          position: absolute;
          right: 2px;
          top: 6px;
          font-size: 12px;
          line-height: 1;
          color: rgba(201, 203, 224, 0.65);
        }

        &:hover,
        &:focus-visible {
          background: rgba(255, 255, 255, 0.06);
          outline: none;
        }

        &.openMenu {
          background: rgba(255, 255, 255, 0.08);
        }

        &.activeMenu {
          background: rgba(32, 96, 234, 0.22);
          color: #fff;
          box-shadow: inset 3px 0 0 #2060ea;

          .menuLabel {
            color: #fff;
            font-weight: 600;
          }

          .submenuChevron {
            color: rgba(255, 255, 255, 0.75);
          }
        }

        &:active {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
`;
